<template>
  <div
    :is="formName"
    :key="formName"
    v-bind="{...$attrs, isPublisher, isPubManager, availableCountries }"
    :submitting="submitting"
    v-on="$listeners"
    @input="onInput"
  />
</template>

<script>
import {
  ATTACH_PUBLISHER_TO_NUMBER,
  CREATE_NUMBER,
  CREATE_NUMBER_ORDER,
  EDIT_NUMBER,
  GET_NUMBER_AVAILABLE_COUNTRIES
} from '@/modules/number/store/actionTypes';
import { mapActions, mapGetters, mapState } from 'vuex';
import NumberAllocationFormVue from './NumberAllocationForm.vue';
import NumberFormVue from './NumberForm.vue';
import { getActionStatusName, STATUS_ERROR, STATUS_SUCCESS } from '@/store/helpers';
import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import messages from '../../locales';
import sharedMessages from '@/core/locales';
import { defineComponent } from 'vue';
/**
 * This will be used for the entire page,
 * currently used to separate the business logic.
 *
 * In order to not have to make a component for each form,
 * this will house all of the forms of the page, and will provide a property that
 * determines which form will show.
 *
 * Each form will emit form data, which will be handled in this component.
 */
export default defineComponent({
  components: {
    create: NumberAllocationFormVue,
    edit: NumberFormVue
  },
  props: {
    formName: {
      type: String,
      default: 'create'
    },
  },
  created() {
    if (this.numberCountries.length === 0) {
      this.GET_NUMBER_AVAILABLE_COUNTRIES();
    }
  },
  computed: {
    ...mapState({
      accountId: 'currentAccountId'
    }),
    ...mapState('number', [
      'status',
      'numberCountries'
    ]),
    ...mapGetters({
      isPublisher: 'isPublisher',
      isPubManager: 'isPublisherGroupUser'
    }),
    ...mapGetters('number', [
      'createNumberStatus',
      'createNumberOrderStatus',
      'editNumberStatus'
    ]),
    submitting() {
      const editStatus =
        this.status[getActionStatusName(EDIT_NUMBER)]?.status === 'pending';
      const createStatus =
        this.status[getActionStatusName(CREATE_NUMBER)]?.status === 'pending'
        || this.status[getActionStatusName(CREATE_NUMBER_ORDER)]?.status === 'pending'
        || this.status[getActionStatusName(ATTACH_PUBLISHER_TO_NUMBER)]?.status === 'pending';

      return this.editing ? editStatus : createStatus;
    },
    editing() {
      return this.formName !== 'create';
    },
    availableCountries() {
      return this.numberCountries.map(country => ({
        label: country.name,
        value: country.countryCode,
      }));
    }
  },
  methods: {
    ...mapActions('number', [CREATE_NUMBER, CREATE_NUMBER_ORDER, EDIT_NUMBER, ATTACH_PUBLISHER_TO_NUMBER, GET_NUMBER_AVAILABLE_COUNTRIES]),

    /**
     * Based on which form is showing, we dispatch on different actions
     */
    async onInput(formData) {
      const { accountId } = this;

      if (this.editing) {
        await this.EDIT_NUMBER({ accountId, currentNumber: formData });
        if (this.editNumberStatus.status === STATUS_SUCCESS) {
          this.$emit('submit', formData);
        }
        if (this.editNumberStatus.status === STATUS_ERROR) {
          utilities.showToaster(STATUS_ERROR, this.editNumberStatus.error);
        }
      } else {

        const config = formData?.config || {};
        const publisherId = formData.assignmentSettings.affiliateId;

        /**
         * If we need to allocate numbers, then we create a number order instead.
         * We pass the assignmentSettings object in either case
         */
        const result = await (config.needsAllocation ? this.CREATE_NUMBER_ORDER : this.CREATE_NUMBER)({
          accountId: accountId,
          assignmentSettings: formData.assignmentSettings
        });

        if(config.needsAllocation) {
          if (this.createNumberOrderStatus.status === STATUS_SUCCESS) {
            utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('NUMBER_ORDER_CREATE_SUCCESS'));
            this.$emit('submit', formData);
          }
          if (this.createNumberOrderStatus.status === STATUS_ERROR) {
            utilities.showToaster(STATUS_ERROR, this.createNumberOrderStatus.error);
          }
        } else {
          if (this.createNumberStatus.status === STATUS_SUCCESS) {
            /**
             * If we do not need to allocate numbers, we check to see if a publisher was chosen.
             * In this case, we dispatch another call to attach a publisher to the number
             */
            if (config.needsPublisher) {
              const { data } = result;
              await this.ATTACH_PUBLISHER_TO_NUMBER({
                accountId: accountId,
                numberId: data?.number?.id,
                publisherId
              });
            }
            utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('NUMBER_CREATE_SUCCESS'));
            this.$emit('submit', formData);
          }
          if (this.createNumberStatus.status === STATUS_ERROR) {
            utilities.showToaster(STATUS_ERROR, this.createNumberStatus.error);
          }
        }
      }
    }
  },
  i18n: {
    messages,
    sharedMessages
  }
});

</script>
